import { applySnapshot, getSnapshot } from 'mobx-state-tree';
import dayjs from 'dayjs';

const MAX_CART_VALIDITY_MINUTES = 5;
const CART_KEY = '__checkout';
const GIFT_CART_KEY = '__gift-card-checkout';

const persistGiftCardCart = (rootStore, orderInput) => {
  const { CartStore, FeatureFlagStore, PaymentStore } = rootStore;
  const storedAt = Date.now();
  const snapshots = {
    cart: getSnapshot(CartStore),
    payment: getSnapshot(PaymentStore),
    featureFlags: getSnapshot(FeatureFlagStore),
    storedAt,
    orderInput,
  };

  window.sessionStorage.setItem(GIFT_CART_KEY, JSON.stringify(snapshots));
};

const restoreGiftCardCart = rootStore => {
  const { CartStore, FeatureFlagStore, PaymentStore } = rootStore;
  const storedSnapshots = window.sessionStorage.getItem(GIFT_CART_KEY);

  if (!storedSnapshots) {
    return null;
  }

  const parsedSnapshots = JSON.parse(storedSnapshots);

  const { cart, featureFlags, payment, storedAt, orderInput } = parsedSnapshots;

  if (dayjs().diff(dayjs(storedAt), 'minutes') >= MAX_CART_VALIDITY_MINUTES) {
    return null;
  }

  applySnapshot(CartStore, cart);
  applySnapshot(PaymentStore, payment);
  applySnapshot(FeatureFlagStore, featureFlags);

  return orderInput;
};

const persistCart = rootStore => {
  const {
    CartStore,
    DeliveryStore,
    FeatureFlagStore,
    OfferStore,
    PaymentStore,
    OrderStore,
    OrderingContextStore,
    MenuStore,
  } = rootStore;
  const storedAt = Date.now();

  // Store the current menu to avoid menu sync issues during restoration
  const currentMenu = MenuStore.menu;
  const menuSnapshot = currentMenu ? getSnapshot(currentMenu) : null;

  // Filter out "menu.products" to reduce snapshot size
  const currentOrderItemProductIds = CartStore.orderItems.map(item => item.product.id);
  const filteredMenu = menuSnapshot
    ? {
        ...menuSnapshot,
        products: menuSnapshot.products.filter(product => currentOrderItemProductIds.includes(product.id)),
      }
    : null;

  const snapshots = {
    cart: getSnapshot(CartStore),
    delivery: getSnapshot(DeliveryStore),
    offerStore: getSnapshot(OfferStore),
    payment: getSnapshot(PaymentStore),
    featureFlags: getSnapshot(FeatureFlagStore),
    orderingContext: getSnapshot(OrderingContextStore),
    menu: filteredMenu,
    storedAt,
    payableTotal: OrderStore.payableTotal,
  };

  window.sessionStorage.setItem(CART_KEY, JSON.stringify(snapshots));
};

const getSessionStorageSnapshot = () => {
  const sessionSnapshot = window.sessionStorage.getItem(CART_KEY);
  return sessionSnapshot ? JSON.parse(sessionSnapshot) : undefined;
};

const restoreCart = async rootStore => {
  const sessionSnapshot = getSessionStorageSnapshot();

  if (!sessionSnapshot) {
    return;
  }

  const { storedAt } = sessionSnapshot;

  if (dayjs().diff(dayjs(storedAt), 'minutes') >= MAX_CART_VALIDITY_MINUTES) {
    return;
  }

  // Use the new atomic action for restoration
  await rootStore.restorePreviousSession(sessionSnapshot);
};

const clearPersistedCartData = () => {
  window.sessionStorage.removeItem(CART_KEY);
};

const clearPersistedGiftCartData = () => {
  window.sessionStorage.removeItem(GIFT_CART_KEY);
};

export {
  persistCart,
  restoreCart,
  getSessionStorageSnapshot,
  clearPersistedCartData,
  persistGiftCardCart,
  restoreGiftCardCart,
  clearPersistedGiftCartData,
  CART_KEY,
};
